import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Target, 
  Lightbulb, 
  Globe,
  MapPin,
  Users,
  Award
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

const glocalBenefits = [
  'Navigate the nuances of local health landscapes with cultural and political agility.',
  'Translate global best practices into contextually relevant solutions.',
  'Serve as a bridge for international donors and national governments seeking Value Innovation.'
];

export const AboutPage = () => {
  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="section-padding pt-32 bg-gradient-to-br from-slate-50 to-teal-50" data-testid="about-hero">
        <div className="container-main">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            <motion.span 
              variants={itemVariants}
              className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-6 block"
            >
              About HSL
            </motion.span>
            
            <motion.h1 
              variants={itemVariants}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-6"
            >
              Architecting a New Frontier in Global Health
            </motion.h1>
            
            <motion.p 
              variants={itemVariants}
              className="text-lg md:text-xl text-slate-600 leading-relaxed"
            >
              Health Strategy and Leadership (HSL) is not a traditional consulting firm; 
              we are a strategic partner in systemic transformation. We exist to solve the most 
              persistent challenge in global health: <span className="text-teal-600 font-semibold">the 
              gap between ambitious policy and sustainable impact</span>.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="section-padding bg-white" data-testid="vision-mission">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-teal-600 text-white p-10 lg:p-12"
              data-testid="vision-card"
            >
              <div className="w-14 h-14 bg-white/20 flex items-center justify-center mb-6">
                <Target size={28} className="text-white" />
              </div>
              <span className="font-mono text-xs uppercase tracking-widest text-teal-200 mb-3 block">
                Our Vision
              </span>
              <p className="text-white text-xl md:text-2xl font-heading leading-relaxed">
                To be the globally recognized catalyst for health transformation, creating a world 
                where every health system is built by <span className="font-bold">design</span>, 
                not by default.
              </p>
            </motion.div>

            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-slate-50 border border-slate-200 p-10 lg:p-12"
              data-testid="mission-card"
            >
              <div className="w-14 h-14 bg-slate-200 flex items-center justify-center mb-6">
                <Lightbulb size={28} className="text-slate-600" />
              </div>
              <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-3 block">
                Our Mission
              </span>
              <p className="text-slate-700 text-xl md:text-2xl font-heading leading-relaxed">
                To architect resilient health systems through intentional design, value innovation, 
                and deep analytical rigor. We empower stakeholders to bypass systemic bottlenecks 
                and deliver equitable, high-impact outcomes.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-padding bg-slate-50" data-testid="philosophy-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
                The HSL Philosophy
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Leadership by Design
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Our work is grounded in the conviction that the "Default" state of most health 
                systems—reactive, fragmented, and inefficient—can be overridden. We employ the 
                <span className="text-teal-600 font-semibold"> Ambidextrous Strategy</span>, allowing 
                our clients to maintain operational stability while simultaneously exploring the 
                frontiers of Systemic Leapfrogging.
              </p>
              <p className="text-slate-500 leading-relaxed">
                By integrating advanced analytical methodologies, we provide insights that respect 
                deep local context while meeting the most stringent international standards of evidence.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-[4/3] overflow-hidden rounded-lg shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1653669487404-09c3617c2b6c?w=800&q=80"
                  alt="Black African Healthcare Leaders in Discussion"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="section-padding bg-white" data-testid="founder-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-slate-50 border border-slate-200 p-8 lg:p-12"
          >
            <div className="grid lg:grid-cols-3 gap-12">
              <div className="lg:col-span-1">
                <div className="aspect-square bg-slate-200 overflow-hidden mb-6 rounded-lg">
                  <img
                    src="https://customer-assets.emergentagent.com/job_3adfc79a-813b-4221-b6be-82bded0a937b/artifacts/wbc14tj8_Charles%20CEO.jpg"
                    alt="Dr. Charles Umar - HSL Founder"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex gap-4">
                  <Award className="text-teal-600" size={24} />
                  <Users className="text-teal-600" size={24} />
                  <Globe className="text-teal-600" size={24} />
                </div>
              </div>
              
              <div className="lg:col-span-2">
                <span className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-4 block">
                  The Founder
                </span>
                <h3 className="font-heading text-3xl text-slate-900 mb-2">Dr. Charles Umar</h3>
                <p className="text-slate-500 mb-6">Distinguished Specialist in Program Evaluation and Strategic Leadership</p>
                
                <p className="text-slate-600 leading-relaxed mb-6">
                  HSL is led by Dr. Charles Umar, a distinguished specialist in Program Evaluation and Strategic 
                  Leadership. With a career dedicated to the intersection of governance and health outcomes, 
                  Dr. Charles Umar founded HSL on a singular premise: that the health sector requires a new class 
                  of "Ambidextrous Leaders."
                </p>
                
                <p className="text-slate-500 leading-relaxed">
                  Under his guidance, HSL has evolved into a firm that combines the academic rigor of 
                  an evaluation house with the agile execution of a management consultancy. Dr. Umar's 
                  approach is rooted in the belief that a system is only as resilient as its leaders, 
                  and leadership is a skill that must be designed, not left to luck.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Global-Local Mandate */}
      <section className="section-padding bg-slate-50" data-testid="glocal-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mb-16"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-4 block">
              Our Global-Local (Glocal) Mandate
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
              Headquarters: Abuja, Nigeria
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              Strategically located in Abuja, HSL stands at the crossroads of the Global South's 
              most complex challenges and the Global North's highest technical standards. This 
              unique positioning allows us to:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {glocalBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-slate-200 p-6 hover:border-teal-500 hover:shadow-md transition-all"
                data-testid={`glocal-benefit-${index}`}
              >
                <div className="w-10 h-10 bg-teal-100 flex items-center justify-center mb-4">
                  <span className="text-teal-600 font-heading font-bold">{index + 1}</span>
                </div>
                <p className="text-slate-600 leading-relaxed">{benefit}</p>
              </motion.div>
            ))}
          </div>

          {/* Location Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 bg-white border border-slate-200 p-8 flex items-center gap-6"
            data-testid="location-card"
          >
            <div className="w-16 h-16 bg-teal-100 border border-teal-200 flex items-center justify-center flex-shrink-0">
              <MapPin size={28} className="text-teal-600" />
            </div>
            <div>
              <h4 className="text-slate-900 font-heading text-lg mb-1">Our Location</h4>
              <p className="text-slate-600">
                No 4, Natasha Apoti Street, Kado District, Abuja, Nigeria
              </p>
              <p className="text-slate-500 text-sm mt-1">
                Bridging Global South challenges with Global North standards
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-teal-600" data-testid="about-cta">
        <div className="container-main text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
              Partner with HSL
            </h2>
            <p className="text-teal-100 text-lg mb-8 max-w-2xl mx-auto">
              Join us in architecting a new frontier of global health excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-teal-600 px-8 py-4 font-bold tracking-wide uppercase text-sm hover:bg-teal-50 transition-colors inline-flex items-center justify-center gap-2 group"
                data-testid="about-cta-primary"
              >
                Start a Conversation
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/services"
                className="bg-transparent text-white border border-white/50 px-8 py-4 font-medium uppercase text-sm hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
                data-testid="about-cta-secondary"
              >
                Explore Our Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
